import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { User, LoginForm, Permission } from '../../types';
import apiService from '../../services/api';
import { SessionManager } from '../../utils/sessionManager';
import { isDefinitiveAuthFailure, refreshAccessToken } from '../../services/authRefresh';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  permissions: Permission[];
  lastPermissionUpdate: number | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  permissions: [],
  lastPermissionUpdate: null,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginForm, { rejectWithValue }) => {
    try {
      const response = await apiService.login(credentials.email, credentials.password);
      if (response.success && response.result) {
        SessionManager.setToken(response.result.token);
        SessionManager.setUser(response.result.user);
        return response.result;
      }
      throw new Error('Login failed');
    } catch (error: any) {
      SessionManager.clearSession();
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await apiService.logout();
      SessionManager.clearSession();
      return null;
    } catch (error: any) {
      SessionManager.clearSession();
      return rejectWithValue(error.response?.data?.message || 'Logout failed');
    }
  }
);

/**
 * Why a refresh failed. Only `unauthorized` (the server rejected the token)
 * may end the session - transient failures must leave it intact.
 */
export interface RefreshRejection {
  reason: 'unauthorized' | 'transient';
  message: string;
}

export const refreshToken = createAsyncThunk<
  { token: string },
  void,
  { rejectValue: RefreshRejection }
>('auth/refresh', async (_, { rejectWithValue }) => {
  try {
    // Shared single-flight helper: the axios interceptor uses the same one, so
    // a burst of 401s plus the 30-minute timer still make one refresh call.
    const token = await refreshAccessToken();
    return { token };
  } catch (error: any) {
    if (isDefinitiveAuthFailure(error)) {
      SessionManager.clearSession();
      return rejectWithValue({ reason: 'unauthorized', message: 'Session expired' });
    }
    return rejectWithValue({
      reason: 'transient',
      message: error?.message || 'Token refresh failed',
    });
  }
});

// New thunk for initializing auth state on app start
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const token = SessionManager.getToken();
      if (!token) {
        return null;
      }

      // Check if token is expired
      if (SessionManager.isTokenExpired(token)) {
        SessionManager.clearSession();
        return null;
      }

      // Try to get current user to validate token
      try {
        const response = await apiService.getCurrentUser();
        if (response.success && response.result) {
          return {
            user: response.result,
            token: token
          };
        }
      } catch (apiError: any) {
        // If API call fails (network error, server down, etc.),
        // fall back to localStorage data as a temporary measure
        console.warn('API call failed, falling back to localStorage:', apiError);

        const localUser = SessionManager.getUser();
        if (localUser && token) {
          console.log('Using localStorage user data as fallback');
          return {
            user: localUser,
            token: token
          };
        }

        // If it's a 401 error, try to refresh token
        if (apiError.response?.status === 401) {
          try {
            await dispatch(refreshToken()).unwrap();
            // After successful refresh, try to get user again
            const response = await apiService.getCurrentUser();
            if (response.success && response.result) {
              return {
                user: response.result,
                token: SessionManager.getToken()
              };
            }
          } catch (refreshError) {
            // If refresh also fails, try localStorage as last resort
            const localUser = SessionManager.getUser();
            if (localUser) {
              console.log('Refresh failed, using localStorage as last resort');
              return {
                user: localUser,
                token: SessionManager.getToken()
              };
            }
            SessionManager.clearSession();
            return rejectWithValue('Session expired');
          }
        }

        // For other errors, throw to be handled below
        throw apiError;
      }

      throw new Error('Token validation failed');
    } catch (error: any) {
      // Final fallback: try localStorage one more time
      const localUser = SessionManager.getUser();
      const token = SessionManager.getToken();

      if (localUser && token && !SessionManager.isTokenExpired(token)) {
        console.log('Final fallback: using localStorage data');
        return {
          user: localUser,
          token: token
        };
      }

      SessionManager.clearSession();
      return rejectWithValue(error.response?.data?.message || 'Authentication failed');
    }
  }
);

export const fetchUserPermissions = createAsyncThunk(
  'auth/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getUserPermissions();
      if (response.success && response.result) {
        return response.result;
      }
      throw new Error('Failed to fetch permissions');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch permissions');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getCurrentUser();
      if (response.success && response.result) {
        return response.result;
      }
      throw new Error('Failed to fetch current user');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch current user');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      // Update permissions if user has them
      if (action.payload.permissions) {
        state.permissions = action.payload.permissions;
        state.lastPermissionUpdate = Date.now();
      }
    },
    setPermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload;
      state.lastPermissionUpdate = Date.now();
    },
    setCredentials: (state, action: PayloadAction<{ user: User; token: string; permissions?: Permission[] }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
      if (action.payload.permissions) {
        state.permissions = action.payload.permissions;
        state.lastPermissionUpdate = Date.now();
      }
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.permissions = [];
      state.lastPermissionUpdate = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize auth
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.error = null;
          // Set permissions if available
          if (action.payload.user.permissions) {
            state.permissions = action.payload.user.permissions;
            state.lastPermissionUpdate = Date.now();
          }
        } else {
          // No token found, user is not authenticated
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          state.permissions = [];
          state.lastPermissionUpdate = null;
        }
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.lastPermissionUpdate = null;
        state.error = action.payload as string;
      })
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        // Set permissions if available
        if (action.payload.user.permissions) {
          state.permissions = action.payload.user.permissions;
          state.lastPermissionUpdate = Date.now();
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.lastPermissionUpdate = null;
        state.error = null;
      })
      // Refresh token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        // Network errors, timeouts and 5xx (incl. the backend's 503 for infra
        // failures) keep the session; only a server verdict signs the user out.
        if (action.payload?.reason !== 'unauthorized') {
          return;
        }
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.lastPermissionUpdate = null;
      })
      // Fetch user permissions
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload.permissions || [];
        state.lastPermissionUpdate = Date.now();
      })
      .addCase(fetchUserPermissions.rejected, (_, action) => {
        console.error('Failed to fetch permissions:', action.payload);
      })
      // Get current user
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
        if (action.payload.permissions) {
          state.permissions = action.payload.permissions;
          state.lastPermissionUpdate = Date.now();
        }
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.lastPermissionUpdate = null;
      });
  },
});

export const {
  clearError,
  setUser,
  setPermissions,
  setCredentials,
  clearAuth
} = authSlice.actions;

export default authSlice.reducer;
